/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useEffect, forwardRef, useImperativeHandle, useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Stack } from '@mui/material';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';

import { CostPriceList } from '../../../features/cost-price/components/CostPriceList';
import TestIds from '../../../testIds';
import { FormControlUnitPrice } from './FormControlUnitPrice';
import { FormControlReasonCode } from './FormControlReasonCode';
import { FormControlReasonNote } from './FormControlReasonNote';
import { getFormattedUnitPrice } from '../../../util/formatting';

import styles from './UnitPriceForm.module.scss';

export enum EUnitPriceFields {
  UnitPrice = 'unitPrice',
  ReasonCode = 'reasonCode',
  ReasonNote = 'reasonNote',
}

export type TUnitPriceFormData = {
  [key in EUnitPriceFields]?: string;
};

export interface IUnitPriceState {
  unitPrice: number | undefined;
  reasonCode: string | undefined | null;
  reasonNote: string | undefined | null;
}

export interface IUnitPriceFormRef {
  submit: () => Promise<void>;
  getFormData: () => TUnitPriceFormData | null;
  reset: () => void;
}

export interface IUnitPriceFormProps {
  productId?: number;
  unitPriceState?: IUnitPriceState;
  validateAllFieldsOnSubmit?: boolean;
  onFormChange: (updatedUnitPriceState?: IUnitPriceState) => void;
  onSubmit: (data: TUnitPriceFormData) => void;
  onRevertChanges: () => void;
}

export const UnitPriceForm = forwardRef<IUnitPriceFormRef, IUnitPriceFormProps>(
  (
    {
      productId,
      unitPriceState,
      validateAllFieldsOnSubmit,
      onFormChange,
      onSubmit,
      onRevertChanges,
    },
    ref,
  ): JSX.Element | null => {
    const [originalUnitPrice, setOriginalUnitPrice] = useState<number | undefined>();
    const [showReasonAndComments, setShowReasonAndComments] = useState<boolean>(false);

    const hasUserEditedPrice = useRef<boolean>(false);

    const {
      control,
      watch,
      trigger,
      getValues,
      setValue,
      reset,
      clearErrors,
      formState: { touchedFields },
    } = useForm<TUnitPriceFormData>({
      mode: 'onSubmit',
      defaultValues: {
        unitPrice: '',
        reasonCode: '',
        reasonNote: '',
      },
    });

    const watchedValues: TUnitPriceFormData = watch();

    useEffect((): void => {
      hasUserEditedPrice.current = false;
      setOriginalUnitPrice(unitPriceState?.unitPrice);

      setValue(EUnitPriceFields.UnitPrice, getFormattedUnitPrice(unitPriceState?.unitPrice) || '');
      setValue(EUnitPriceFields.ReasonCode, unitPriceState?.reasonCode || '');
      setValue(EUnitPriceFields.ReasonNote, unitPriceState?.reasonNote || '');

      setShowReasonAndComments(
        !!(unitPriceState?.reasonCode || unitPriceState?.reasonNote || touchedFields.unitPrice),
      );
    }, [
      unitPriceState?.unitPrice,
      unitPriceState?.reasonCode,
      unitPriceState?.reasonNote,
      setValue,
    ]);

    useEffect((): void => {
      onFormChange({
        unitPrice: watchedValues.unitPrice ? parseFloat(watchedValues.unitPrice) : undefined,
        reasonCode: watchedValues.reasonCode || undefined,
        reasonNote: watchedValues.reasonNote || undefined,
      });
    }, [watchedValues, onFormChange]);

    useImperativeHandle(
      ref,
      (): IUnitPriceFormRef => ({
        submit: async () => {
          (await trigger()) && onSubmit(getValues());
        },
        getFormData: getValues,
        reset,
      }),
    );

    const hasUnitPriceChanged = useMemo(
      (): boolean => (watchedValues.unitPrice || '') !== getFormattedUnitPrice(originalUnitPrice),
      [watchedValues.unitPrice, originalUnitPrice],
    );

    const validateField =
      (errorMessage: string) =>
      (value?: string): string | true => {
        if ((validateAllFieldsOnSubmit || hasUnitPriceChanged) && !value?.trim()) {
          return errorMessage;
        }

        return true;
      };

    return unitPriceState ? (
      <Stack
        component='form'
        autoComplete='off'
        data-testid={TestIds.components.forms.unitPriceForm.form}
      >
        <Stack spacing='0.4rem'>
          <FormControlUnitPrice
            control={control}
            validateField={validateField}
            onFocus={() => setShowReasonAndComments(true)}
            clearError={() => clearErrors(EUnitPriceFields.UnitPrice)}
          />

          <Box display='flex' justifyContent='end'>
            <Button
              variant='farmlands2.0_typography-default'
              color='primary'
              data-testid={TestIds.components.forms.unitPriceForm.revertChangesBtn}
              onClick={onRevertChanges}
              className={styles.flUnitPriceForm__revertChangesBtn}
              startIcon={
                <ChangeCircleOutlinedIcon
                  className={styles.flUnitPriceForm__revertChangesBtn__icon}
                />
              }
            >
              Revert changes
            </Button>
          </Box>
        </Stack>

        {showReasonAndComments && (
          <Stack spacing='1.9rem' marginBottom='0.5rem'>
            <FormControlReasonCode
              control={control}
              validateField={validateField}
              clearError={() => clearErrors(EUnitPriceFields.ReasonCode)}
            />

            <FormControlReasonNote
              control={control}
              validateField={validateField}
              clearError={() => clearErrors(EUnitPriceFields.ReasonNote)}
            />
          </Stack>
        )}

        {productId && (
          <Box marginTop='1rem'>
            <CostPriceList productId={productId} defaultExpanded={true} />
          </Box>
        )}
      </Stack>
    ) : null;
  },
);
